
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Link2, AlertTriangle } from "lucide-react";

interface LinkReservationDialogProps {
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

const LinkReservationDialog = ({ onSuccess, trigger }: LinkReservationDialogProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reservationNumber, setReservationNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLinkReservation = async () => {
    if (!user) {
      setError("Vous devez être connecté pour lier une réservation");
      return;
    }

    if (!reservationNumber.trim()) {
      setError("Veuillez entrer un numéro de réservation");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Vérifier si la réservation existe
      const { data: reservation, error: fetchError } = await supabase
        .from('reservations')
        .select('*')
        .eq('id', reservationNumber.trim())
        .single();

      if (fetchError || !reservation) {
        throw new Error("Numéro de réservation introuvable");
      }

      // Vérifier si la réservation est déjà liée à un compte
      if (reservation.email === user.email) {
        setError("Cette réservation est déjà liée à votre compte");
        return;
      }

      // Mettre à jour l'email de la réservation
      const { error: updateError } = await supabase
        .from('reservations')
        .update({ email: user.email })
        .eq('id', reservationNumber.trim());

      if (updateError) {
        throw updateError;
      }

      toast.success("Réservation liée avec succès à votre compte");
      setOpen(false);
      setReservationNumber("");
      setError(null);
      onSuccess?.();
    } catch (error: any) {
      console.error("Erreur lors de la liaison de la réservation:", error);
      setError(error.message || "Erreur lors de la liaison de la réservation");
    } finally {
      setLoading(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" className="flex items-center gap-2">
      <Link2 className="h-4 w-4" />
      Lier une réservation
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Lier une réservation
          </DialogTitle>
          <DialogDescription>
            Entrez le numéro de votre réservation pour la lier à votre compte
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="reservation-number">Numéro de réservation</Label>
            <Input
              id="reservation-number"
              value={reservationNumber}
              onChange={(e) => setReservationNumber(e.target.value)}
              placeholder="Entrez votre numéro de réservation"
              disabled={loading}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={handleLinkReservation}
              disabled={loading || !reservationNumber.trim()}
              className="flex-1"
            >
              {loading ? "Liaison en cours..." : "Lier la réservation"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Annuler
            </Button>
          </div>

          <p className="text-sm text-gray-600 text-center">
            Le numéro de réservation vous a été envoyé par email lors de votre réservation.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkReservationDialog;
